if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (!this.hasAttribute('data-rnf-editorial-gallery') || !window.IntersectionObserver) return;
        this.storyObserver?.disconnect();
        this.visibleVideos = new Set();
        this.videoEvents?.abort();
        this.videoEvents = new AbortController();
        this.storyObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
              this.visibleVideos.add(entry.target);
              if (!document.hidden) this.playInlineVideo(entry.target);
            } else {
              this.visibleVideos.delete(entry.target);
              entry.target.querySelector('video')?.pause();
            }
          });
        }, { threshold: [0, 0.25] });
        this.querySelectorAll('[data-rnf-autoplay-video]').forEach((item) => this.storyObserver.observe(item));
        document.addEventListener('visibilitychange', () => {
          this.visibleVideos.forEach((item) => {
            if (document.hidden) item.querySelector('video')?.pause();
            else this.playInlineVideo(item);
          });
        }, { signal: this.videoEvents.signal });
      }

      playInlineVideo(item) {
        const deferred = item.querySelector('deferred-media');
        if (!deferred) return;
        if (!deferred.hasAttribute('loaded')) {
          const source = deferred.querySelector('template')?.content.querySelector('video');
          if (!source) return;
          const video = source.cloneNode(true);
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          deferred.appendChild(video);
          deferred.setAttribute('loaded', 'true');
        }
        const video = deferred.querySelector('video');
        if (!video || !this.visibleVideos.has(item) || document.hidden) return;
        video.muted = true;
        video.playsInline = true;
        video.play()?.catch(() => {}); // Native controls remain available if autoplay is blocked.
      }

      disconnectedCallback() {
        this.storyObserver?.disconnect();
        this.videoEvents?.abort();
        this.visibleVideos?.forEach((item) => item.querySelector('video')?.pause());
        this.visibleVideos?.clear();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Desktop editorial media follows normal page scrolling; variant changes
        // replace the sticky preview without sending the customer back up the page.
        const editorialDesktop = this.hasAttribute('data-rnf-editorial-gallery') && window.matchMedia('(min-width: 1280px)').matches;
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (editorialDesktop) return;
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (options?.preventScroll === true) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        if (this.announcementImage && this.announcementHandler) {
          this.announcementImage.removeEventListener('load', this.announcementHandler);
        }
        clearTimeout(this.announcementTimer);
        this.announcementImage = image;
        this.announcementHandler = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          this.announcementTimer = setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete && image.naturalWidth > 0) this.announcementHandler();
        else image.addEventListener('load', this.announcementHandler, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
        if (this.visibleVideos?.has(activeItem)) this.playInlineVideo(activeItem);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
